<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? doctorDetails ? &quot;Doctor's Council Certificates&quot; : &quot;Council Registration Certificates&quot; : &quot;Deleted Certificates&quot;"
              :subtitle="isDeletePage() != true ? doctorDetails ? &quot;View and manage this doctor's medical council registrations&quot; : &quot;Manage and monitor medical council registrations&quot; : &quot;Restore or permanently delete certificates&quot;"
            >
              <template v-slot:actions>
                <div v-if="isDeletePage() != true" class="d-flex justify-end align-center">
                    <v-btn
                      color="primary"
                      elevation="1"
                      small
                      :to="
                        doctorDetails
                          ? `/council_registration_certificates/create?doctor_id=${doctorId}`
                          : '/council_registration_certificates/create'
                      "
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Certificate
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Certificates</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Certificates"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    placeholder="Council name, registration number..."
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.is_active"
                    :items="statuses"
                    label="Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Verification Status Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.status"
                    :items="verificationStatuses"
                    label="Verification"
                    prepend-inner-icon="mdi-certificate"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Expiry Status Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.expired"
                    :items="expiryStatuses"
                    label="Expiry Status"
                    prepend-inner-icon="mdi-calendar-clock"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>
              </v-row>

              <!-- Doctor Details Section (when viewing doctor-specific page) -->
              <v-row v-if="doctorDetails" class="mt-3">
                <v-col cols="12">
                  <v-divider class="mb-3"></v-divider>
                  <div class="pa-4 doctor-info-section">
                    <v-container fluid class="pa-0">
                      <v-row align="center" no-gutters>
                        <!-- Left: Avatar and Name -->
                        <v-col cols="12" md="4" class="pr-4">
                          <div class="d-flex align-center">
                            <v-avatar size="48" class="elevation-2 mr-3">
                              <v-img
                                v-if="doctorDetails.image_path"
                                :src="getImageUrl(doctorDetails.image_path)"
                                :alt="doctorDetails.first_name"
                              ></v-img>
                              <v-icon v-else color="primary" size="24">mdi-doctor</v-icon>
                            </v-avatar>
                            <div class="flex-grow-1">
                              <div class="text-h6 font-weight-bold primary--text">
                                {{ getDoctorName(doctorDetails) }}
                              </div>
                              <div class="text-body-2 grey--text">
                                @{{ doctorDetails.user_name || 'N/A' }}
                              </div>
                            </div>
                          </div>
                        </v-col>

                        <!-- Center: Contact Information -->
                        <v-col cols="12" md="5" class="px-3">
                          <v-container fluid class="pa-0">
                            <v-row dense>
                              <v-col cols="12">
                                <div class="contact-row">
                                  <v-icon color="primary" size="16" class="mr-2">mdi-email</v-icon>
                                  <span class="text-body-2">{{ doctorDetails.email }}</span>
                                </div>
                              </v-col>
                              <v-col cols="12" v-if="doctorDetails.phone">
                                <div class="contact-row">
                                  <v-icon color="primary" size="16" class="mr-2">mdi-phone</v-icon>
                                  <span class="text-body-2">{{ doctorDetails.phone }}</span>
                                </div>
                              </v-col>
                            </v-row>
                          </v-container>
                        </v-col>

                        <!-- Right: Status and Actions -->
                        <v-col cols="12" md="3" class="pl-3">
                          <div class="status-actions-section">
                            <div class="d-flex justify-end mb-2">
                              <v-chip color="success" x-small>
                                <v-icon left x-small>mdi-shield-check</v-icon>
                                Verified Doctor
                              </v-chip>
                            </div>
                            <div class="d-flex justify-end">
                              <v-btn
                                small
                                color="primary"
                                outlined
                                :to="`/users/${doctorId}`"
                                class="px-3"
                              >
                                <v-icon left x-small>mdi-account-details</v-icon>
                                Profile
                              </v-btn>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                    </v-container>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Certificates Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-table</v-icon
                    >
                    <span class="text-body-1 font-weight-medium"
                      >Certificates List</span
                    >
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-certificate</v-icon>
                      {{ count }}
                      {{ count === 1 ? "Certificate" : "Certificates" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-2">
                      <span class="text-caption grey--text mr-1">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn x-small outlined :value="10">10</v-btn>
                        <v-btn x-small outlined :value="25">25</v-btn>
                        <v-btn x-small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>

                    <!-- Export Button -->
                    <v-btn
                      v-if="!isDeletePage()"
                      x-small
                      color="success"
                      outlined
                      @click="exportToExcel"
                      class="px-2"
                    >
                      <v-icon left x-small>mdi-microsoft-excel</v-icon>
                      Export
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader message="Loading certificates..." />
                </div>
              </section>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="certificates"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- Status Column -->
                <template #[`item.is_active`]="{ item }">
                  <v-chip
                    :color="item.is_active ? 'success' : 'error'"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{
                        item.is_active ? "mdi-check-circle" : "mdi-close-circle"
                      }}
                    </v-icon>
                    {{ item.is_active ? "Active" : "Inactive" }}
                  </v-chip>
                </template>

                <!-- Doctor Info Column -->
                <template #[`item.doctor_info`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2">
                      <v-img
                        v-if="item.user && item.user.image_path"
                        :src="getImageUrl(item.user.image_path)"
                        :alt="item.user.first_name"
                      ></v-img>
                      <v-icon v-else color="grey lighten-1" size="18"
                        >mdi-doctor</v-icon
                      >
                    </v-avatar>
                    <div>
                      <div class="text-body-2 font-weight-medium">
                        {{ getDoctorName(item.user) }}
                      </div>
                      <div class="text-caption grey--text">
                        {{ item.user ? item.user.email : "No doctor assigned" }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Council & Registration Column -->
                <template #[`item.council_info`]="{ item }">
                  <div>
                    <div class="text-body-2 font-weight-medium">
                      {{ item.council_name || "N/A" }}
                    </div>
                    <div class="text-caption primary--text font-weight-medium">
                      {{ item.registration_number || "N/A" }}
                    </div>
                    <div
                      class="text-caption grey--text"
                      v-if="item.registration_year"
                    >
                      Year: {{ item.registration_year }}
                    </div>
                  </div>
                </template>

                <!-- Verification Status Column -->
                <template #[`item.status`]="{ item }">
                  <v-chip
                    :color="item.status ? 'success' : 'warning'"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{
                        item.status ? "mdi-check-decagram" : "mdi-clock-alert"
                      }}
                    </v-icon>
                    {{ item.status ? "Verified" : "Pending" }}
                  </v-chip>
                  <div
                    class="text-caption grey--text mt-1"
                    v-if="item.verified_at"
                  >
                    {{ formatDate(item.verified_at) }}
                  </div>
                </template>

                <!-- Expiry Status Column -->
                <template #[`item.expiry_status`]="{ item }">
                  <div>
                    <v-chip
                      :color="getExpiryColor(item)"
                      x-small
                      dark
                      class="mb-1"
                    >
                      <v-icon left x-small>{{ getExpiryIcon(item) }}</v-icon>
                      {{ getExpiryLabel(item) }}
                    </v-chip>
                    <div
                      class="text-caption grey--text"
                      v-if="item.expiry_date"
                    >
                      {{ formatDate(item.expiry_date) }}
                    </div>
                  </div>
                </template>

                <!-- Certificate File Column -->
                <template #[`item.certificate_path`]="{ item }">
                  <v-btn
                    v-if="item.certificate_path"
                    x-small
                    color="info"
                    outlined
                    @click="viewCertificate(item)"
                  >
                    <v-icon left x-small>mdi-file-pdf-box</v-icon>
                    View
                  </v-btn>
                  <span v-else class="text-caption grey--text">No file</span>
                </template>

                <!-- Actions Column -->
                <template #[`item.actions`]="{ item }">
                  <div class="d-flex justify-center">
                    <template v-if="!isDeletePage()">
                      <!-- View/Edit Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="primary"
                            :to="`/council_registration_certificates/${encryptIt(
                              item.id
                            )}`"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>View/Edit</span>
                      </v-tooltip>

                      <!-- Actions Menu -->
                      <v-menu bottom left>
                        <template #activator="{ on, attrs }">
                          <v-btn x-small color="info" v-bind="attrs" v-on="on">
                            <v-icon x-small>mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>

                        <v-list dense>
                          <!-- Verify/Unverify (Admin only) -->
                          <v-list-item
                            v-if="canVerify"
                            @click="toggleVerification(item)"
                          >
                            <v-list-item-icon class="mr-2">
                              <v-icon
                                x-small
                                :color="item.status ? 'orange' : 'green'"
                              >
                                {{
                                  item.status
                                    ? "mdi-close-circle"
                                    : "mdi-check-circle"
                                }}
                              </v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-body-2">
                                {{
                                  item.status
                                    ? "Mark Unverified"
                                    : "Mark Verified"
                                }}
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>

                          <!-- Toggle Status -->
                          <v-list-item @click="toggleStatus(item)">
                            <v-list-item-icon class="mr-2">
                              <v-icon
                                x-small
                                :color="item.is_active ? 'orange' : 'green'"
                              >
                                {{ item.is_active ? "mdi-pause" : "mdi-play" }}
                              </v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-body-2">
                                {{ item.is_active ? "Deactivate" : "Activate" }}
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>

                          <!-- Delete -->
                          <v-list-item @click="deleteItem(item)">
                            <v-list-item-icon class="mr-2">
                              <v-icon x-small color="red">mdi-delete</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-body-2">
                                Delete
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </template>

                    <!-- Restore Actions for Deleted Items -->
                    <template v-else>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="success"
                            @click="restoreItem(item)"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-restore</v-icon>
                          </v-btn>
                        </template>
                        <span>Restore</span>
                      </v-tooltip>

                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="error"
                            @click="permanentDelete(item)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-delete-forever</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete Permanently</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <!-- No data template -->
                <template v-slot:no-data>
                  <empty-state icon="mdi-certificate" title="No certificates found" :message="isDeletePage() ? &quot;No deleted certificates&quot; : &quot;Create your first certificate&quot;" />
                </template>
              </v-data-table>

              <!-- Pagination -->
              <v-card-actions
                v-if="count > itemsPerPage"
                class="justify-center"
              >
                <v-pagination
                  v-model="page"
                  :length="Math.ceil(count / itemsPerPage)"
                  :total-visible="7"
                  @input="getData"
                  color="primary"
                ></v-pagination>
              </v-card-actions>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete the certificate for
          <strong>{{ selectedItem?.council_name }}</strong
          >?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="confirmDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Certificate Viewer Dialog -->
    <FileViewer v-model="certificateDialog" :url="currentCertificateUrl" title="Certificate Document" />
  </v-main>
</template>

<script>
import axios from "@/axios";
import { FileViewer } from "@/components";

export default {
  name: "CouncilRegistrationCertificatesIndex",
  components: { FileViewer },
  data() {
    return {
      isLoading: false,
      certificates: [],
      count: 0,
      page: 1,
      itemsPerPage: 15,
      selectedItemsPerPage: 15,
      deleteDialog: false,
      certificateDialog: false,
      currentCertificateUrl: '',
      selectedItem: null,
      doctorId: null,
      doctorDetails: null,

      // Filters
      filter: {
        search_keyword: null,
        is_active: null,
        status: null,
        expired: null,
      },

      // Filter Options
      statuses: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
      verificationStatuses: [
        { text: "Verified", value: true },
        { text: "Pending", value: false },
      ],
      expiryStatuses: [
        { text: "Expired", value: true },
        { text: "Valid", value: false },
      ],

      // Table Headers
      headers: [
        { text: "Sr.", value: "sr_no", width: "60px" },
        { text: "Status", value: "is_active", width: "100px" },
        { text: "Doctor", value: "doctor_info", width: "200px" },
        {
          text: "Council & Registration",
          value: "council_info",
          width: "250px",
        },
        { text: "Verification", value: "status", width: "120px" },
        { text: "Expiry Status", value: "expiry_status", width: "120px" },
        { text: "Certificate", value: "certificate_path", width: "100px" },
        { text: "Actions", value: "actions", width: "120px", sortable: false },
      ],
    };
  },

  computed: {
    hasActiveFilters() {
      return Object.values(this.filter).some(
        (value) => value !== null && value !== undefined
      );
    },

    canVerify() {
      return this.roleName === "Super Admin" || this.roleName === "Admin";
    },
  },

  mounted() {
    this.extractDoctorIdFromRoute();
    this.getData();
  },

  methods: {
    async getData() {
      this.isLoading = true;

      try {
        const params = {
          page: this.page,
          rowsPerPage: this.itemsPerPage,
          show_deleted: this.isDeletePage(),
          ...this.filter,
        };

        // Add user_id filter if viewing doctor-specific page
        if (this.doctorId) {
          params.user_id = this.decryptIt(this.doctorId);
        }

        const response = await axios.get("/council_registration_certificates", {
          params,
        });

        this.certificates = response.data.data;
        this.count = response.data.count;

        // Extract doctor details from first certificate if viewing doctor-specific page
        if (
          this.doctorId &&
          this.certificates.length > 0 &&
          this.certificates[0].user
        ) {
          this.doctorDetails = this.certificates[0].user;
        }
      } catch (error) {
        console.error("Error fetching certificates:", error);
        this.$toast.error("Failed to load certificates");
      } finally {
        this.isLoading = false;
      }
    },

    extractDoctorIdFromRoute() {
      // Extract doctor ID from route params (e.g., /doctors/:doctorId/council-certificates)
      if (this.$route.params.doctorId) {
        this.doctorId = this.$route.params.doctorId;
      }
    },

    getActiveFiltersCount() {
      return Object.values(this.filter).filter(
        (value) => value !== null && value !== undefined
      ).length;
    },

    clearAllFilters() {
      this.filter = {
        search_keyword: null,
        is_active: null,
        status: null,
        expired: null,
      };
      this.page = 1;
      this.getData();
    },

    changeItemsPerPageToggle(value) {
      if (value !== null) {
        this.itemsPerPage = value;
        this.page = 1;
        this.getData();
      }
    },

    getDoctorName(user) {
      if (!user) return "No doctor assigned";
      return `${user.first_name || ""} ${user.last_name || ""}`.trim() || "N/A";
    },

    getExpiryColor(item) {
      if (!item.expiry_date) return "grey";
      const expiryDate = new Date(item.expiry_date);
      const now = new Date();
      const threeMonthsFromNow = new Date();
      threeMonthsFromNow.setMonth(now.getMonth() + 3);

      if (expiryDate < now) return "error";
      if (expiryDate <= threeMonthsFromNow) return "warning";
      return "success";
    },

    getExpiryIcon(item) {
      if (!item.expiry_date) return "mdi-calendar-question";
      const expiryDate = new Date(item.expiry_date);
      const now = new Date();
      const threeMonthsFromNow = new Date();
      threeMonthsFromNow.setMonth(now.getMonth() + 3);

      if (expiryDate < now) return "mdi-calendar-remove";
      if (expiryDate <= threeMonthsFromNow) return "mdi-calendar-alert";
      return "mdi-calendar-check";
    },

    getExpiryLabel(item) {
      if (!item.expiry_date) return "No Expiry";
      const expiryDate = new Date(item.expiry_date);
      const now = new Date();
      const threeMonthsFromNow = new Date();
      threeMonthsFromNow.setMonth(now.getMonth() + 3);

      if (expiryDate < now) return "Expired";
      if (expiryDate <= threeMonthsFromNow) return "Expires Soon";
      return "Valid";
    },

    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    getImageUrl(imagePath) {
      return this.mediaUrl + "/" + imagePath;
    },

    viewCertificate(certificate) {
      if (!certificate.certificate_path) return;
      this.currentCertificateUrl = this.mediaUrl + "/" + certificate.certificate_path;
      this.certificateDialog = true;
    },

    async toggleVerification(item) {
      try {
        const newStatus = !item.status;
        await axios.put(`/council_registration_certificates/${item.id}`, {
          status: newStatus,
          verified_at: newStatus ? new Date().toISOString() : null,
        });

        this.$toast.success(
          `Certificate ${
            newStatus ? "verified" : "marked as unverified"
          } successfully`
        );
        this.getData();
      } catch (error) {
        console.error("Error updating verification:", error);
        this.$toast.error("Failed to update verification status");
      }
    },

    async toggleStatus(item) {
      try {
        await axios.put(`/council_registration_certificates/${item.id}`, {
          is_active: !item.is_active,
        });

        this.$toast.success(
          `Certificate ${
            !item.is_active ? "activated" : "deactivated"
          } successfully`
        );
        this.getData();
      } catch (error) {
        console.error("Error updating status:", error);
        this.$toast.error("Failed to update status");
      }
    },

    deleteItem(item) {
      this.selectedItem = item;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      try {
        await axios.post(
          `/council_registration_certificates/delete/${this.selectedItem.id}`
        );
        this.$toast.success("Certificate deleted successfully");
        this.deleteDialog = false;
        this.selectedItem = null;
        this.getData();
      } catch (error) {
        console.error("Error deleting certificate:", error);
        this.$toast.error("Failed to delete certificate");
      }
    },

    async restoreItem(item) {
      try {
        await axios.post(
          `/council_registration_certificates/restore/${item.id}`
        );
        this.$toast.success("Certificate restored successfully");
        this.getData();
      } catch (error) {
        console.error("Error restoring certificate:", error);
        this.$toast.error("Failed to restore certificate");
      }
    },

    async permanentDelete(item) {
      if (
        confirm(
          `Permanently delete certificate for ${item.council_name}? This cannot be undone.`
        )
      ) {
        try {
          await axios.delete(`/council_registration_certificates/${item.id}`);
          this.$toast.success("Certificate permanently deleted");
          this.getData();
        } catch (error) {
          console.error("Error permanently deleting certificate:", error);
          this.$toast.error("Failed to permanently delete certificate");
        }
      }
    },

    async exportToExcel() {
      try {
        const params = { ...this.filter };

        // Add user_id filter if viewing doctor-specific page
        if (this.doctorId) {
          params.user_id = this.decryptIt(this.doctorId);
        }

        const response = await axios.get(
          "/council_registration_certificates/excel",
          {
            params,
            responseType: "blob",
          }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `council_certificates_${new Date().toISOString().split("T")[0]}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.$toast.success("Excel file downloaded successfully");
      } catch (error) {
        console.error("Error exporting to Excel:", error);
        this.$toast.error("Failed to export data");
      }
    },
  },

  watch: {
    page() {
      this.getData();
    },
  },
};
</script>

<style scoped>
.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.enhanced-table >>> .v-data-table-header {
  background-color: #f5f5f5;
}

.enhanced-table >>> .v-data-table tbody tr:hover {
  background-color: rgba(33, 150, 243, 0.04);
}

.doctor-info-section {
  background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
  border-radius: 12px;
  border: 1px solid #e3f2fd;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.contact-row {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}

.contact-row:last-child {
  margin-bottom: 0;
}

.status-actions-section {
  min-height: 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
</style>